import {Injectable} from '@angular/core';
import * as moment from 'moment';
import {CartStore} from '../stores/cart.store';
import {ProductVariation, WorkDay, WorkTime} from '../models/restaurant.model';
import {FulfillmentTypeItem} from '../../pages/checkout/models/location.model';
import {FulfillmentType} from '../models/order.model';
import {PaymentStore} from '../stores/payment.store';

@Injectable({
  providedIn: 'root'
})
export class ItemValidateService {

  constructor(private cartStore: CartStore,
              private paymentStore: PaymentStore) {
  }

  public validLeadTime(variation: ProductVariation, hoursOfOperation: WorkDay[]): boolean {
    const currentTime = new Date();
    const pickupTime = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();
    const validatedRanges = [];
    if (!hoursOfOperation ||  !hoursOfOperation[pickupTime?.getDay()]) {
      return !!validatedRanges.find(validRange => validRange);
    }
      for (const [index] of hoursOfOperation[pickupTime.getDay()].startTime.entries()) {
        const storeOpeningTime = hoursOfOperation[pickupTime.getDay()].startTime[index].getDate(pickupTime);
        const storeClosingTime = hoursOfOperation[pickupTime.getDay()].endTime[index].getDate(pickupTime);

        const checkBeforePickupTime = currentTime.getTime() <= pickupTime.getTime() - this.convertFromLeadTime(variation.pickupLeadTime);
        const checkBeforeOpening = this.convertFromLeadTime(variation.openingLeadTime) === 0 ||
          currentTime.getTime() <= storeOpeningTime.getTime() - this.convertFromLeadTime(variation.openingLeadTime);
        const checkBeforeClosing = pickupTime.getTime() <= storeClosingTime.getTime() - this.convertFromLeadTime(variation.closingLeadTime);

        validatedRanges.push(checkBeforePickupTime && checkBeforeOpening && checkBeforeClosing);
      }
    return !!validatedRanges.find(validRange => validRange);
  }

  public validDayRestriction(variation: ProductVariation): boolean {
    const pickupTime = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();

    if (variation.dailyAvailability == null) {
      return true;
    }

    for (const day of variation.dailyAvailability) {
      if (day.value === pickupTime.getDay() && day.enabled) {
        if (day.startTime === null || day.endTime === null) {
          return true;
        }
        const startTime = day.startTime.getHours() * 60 + day.startTime.getMinutes();
        const endTime = day.endTime.getHours() * 60 + day.endTime.getMinutes();
        const pickup = pickupTime.getHours() * 60 + pickupTime.getMinutes();
        return startTime <= pickup && pickup <= endTime;
      }
    }
    return false;
  }

  public validFulfillmentRestriction(variation: ProductVariation, fulfillment: FulfillmentTypeItem, isTab: boolean = false): boolean {
    let fulfillmentType = fulfillment.title;
    if (fulfillment.type === FulfillmentType.HANDOFF) {
      fulfillmentType = 'X-Side';
    }
    if (this.paymentStore.createTabChecked || isTab) {
      fulfillmentType = 'Tabs';
    }
    const variationValidFulfillments = variation.availableInFulfillments.split(',');

    return !!variationValidFulfillments.find((result: string) => { return fulfillmentType.toLowerCase() === result.toLowerCase(); });
  }

  public validItemDatetimeRange(variation: ProductVariation): boolean {
    const pickupTime = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();

    return variation.availableFrom < pickupTime && variation.availableUntil > pickupTime;
  }

  public validFulfillments(variation: ProductVariation, fulfillments: string[]): boolean {
    const variationValidFulfillments = variation.availableInFulfillments.split(',');
    const availableFulfillments = [];

    for (const fulfillment of fulfillments) {
      availableFulfillments.push(!!variationValidFulfillments.find((result: string) => { return fulfillment.toLowerCase() === result.toLowerCase(); }));
    }

    return !!availableFulfillments.find((result: boolean) => { return result === true; });
  }

  public validItemRestaurantWorkHours(hoursOfOperation: WorkDay[]): boolean {
    const pickup = this.cartStore.pickupAt ? new Date(this.cartStore.pickupAt) : new Date();
    const chosenDay = hoursOfOperation.find( (day) => moment(day.date).isSame(pickup, 'day'));
    if (!chosenDay || !chosenDay.enable) {
      return false;
    }
    for (const [index] of chosenDay.startTime.entries()) {
      const rangeStartTime = this.convertWorkTimeToMinutes(chosenDay.startTime[index]);
      const rangeEndTime = this.convertWorkTimeToMinutes(chosenDay.endTime[index]);
      const pickupTime = pickup.getHours() * 60 + pickup.getMinutes();
      if (rangeStartTime <= pickupTime && rangeEndTime >= pickupTime) {
        return true;
      }
    }
    return false;
  }

  public convertLeadTimeToMinutes(value: string): number {
    const timeInMillis = this.convertFromLeadTime(value);
    return Math.ceil(timeInMillis / 60000);
  }

  private convertWorkTimeToMinutes(workTime: WorkTime): number {
    const meridian = workTime.meridian ? 0 : 12;
    const hours = workTime.time.hour === 12 ? 0 : workTime.time.hour;
    return (hours + meridian) * 60 + workTime.time.minute;
  }

  private convertFromLeadTime(value: string): number {
    return moment.duration(value).asMilliseconds();
  }
}
